import { AccountGroupService } from '../account-group';
import { FeatureFlagService } from '../core/feature-flag.service';
import { TranslateService } from '@ngx-translate/core';
import {
  AddToListMultiRowAction,
  AddToListSelectAllAction,
  AddToListSingleRowAction,
  ListActionsService,
} from '@galaxy/crm/integrations/dynamic-lists-actions';
import { combineLatest, map, Observable, switchMap } from 'rxjs';
import { MultiRowAction, SingleRowAction } from '@galaxy/crm/static';
import { PlatformExtensionFieldIds, SystemFieldIds } from '@galaxy/crm/services/field-service';
import { DynamicListsFeature, EphemeralAutomationsFeature } from './constants';
import { BulkDeleteService, BulkDeleteAll, ExportActionsService, ExportSelectAllRowAction } from '@galaxy/crm/dynamic';
import { CRMAccessService } from './access.service';
import {
  AutomationActionsService,
  AutomationMultiRowAction,
  AutomationSelectAllRowAction,
  AutomationSingleRowAction,
  EphemeralAutomationMultiRowAction,
  EphemeralAutomationSelectAllRowAction,
  EphemeralAutomationSingleRowAction,
} from '@galaxy/crm/integrations/automation';
import { Row } from '@vendasta/galaxy/table';
import { SnapshotSidepanelService } from './snapshot-sidepanel/snapshot-sidepanel.service';

export function buildCompanySingleRowTableActions(
  accountGroupService: AccountGroupService,
  featureFlagService: FeatureFlagService,
  listActionsService: ListActionsService,
  accessService: CRMAccessService,
  automationActionService: AutomationActionsService,
  currentUserId$: Observable<string>,
  snapshotSidepanelService: SnapshotSidepanelService,
  isResolvedToPartnerNamespace$: Observable<boolean>,
  namespace$: Observable<string>,
  snapshotName$: Observable<string>,
): Observable<SingleRowAction[]> {
  return accountGroupService.currentAccountGroup$.pipe(
    switchMap((currentAccountGroup) => {
      return combineLatest([
        accountGroupService.currentAccountGroupId$,
        featureFlagService.checkFeatureFlagsMulti(currentAccountGroup.partnerId, '', [
          DynamicListsFeature,
          EphemeralAutomationsFeature,
        ]),
        accessService.canRunManualAutomations$,
        currentUserId$,
        isResolvedToPartnerNamespace$,
        namespace$,
        snapshotName$,
      ]);
    }),
    map(
      ([
        accountGroupId,
        featureFlags,
        canRunAutomations,
        currentUserId,
        isResolvedToPartnerNamespace,
        namespace,
        snapshotName,
      ]) => {
        const canAddToList = featureFlags[DynamicListsFeature];
        const canRunEphemeralAutomations = featureFlags[EphemeralAutomationsFeature];
        const actions: SingleRowAction[] = [];
        if (canAddToList) {
          actions.push(
            AddToListSingleRowAction(accountGroupId, listActionsService, 'Company', 'CRM.ADD_TO_STATIC_LIST'),
          );
        }
        if (isResolvedToPartnerNamespace) {
          actions.push({
            label: snapshotName,
            callback: (row: Row) => {
              snapshotSidepanelService.openSidepanel({
                companyID: row?.data?.[SystemFieldIds.CompanyID]?.value as string,
                partnerID: namespace,
                accountGroupID: row?.data?.[PlatformExtensionFieldIds.AccountGroupID]?.value as string,
                trackingCategory: 'list-company-table',
              });
            },
          });
        }
        if (canRunAutomations) {
          actions.push(
            AutomationSingleRowAction(
              accountGroupId,
              automationActionService,
              'Company',
              'COMPANY.LIST_OBJECTS_TABLE.ACTIONS.START_AUTOMATION',
            ),
          );
          if (canRunEphemeralAutomations) {
            actions.push(
              EphemeralAutomationSingleRowAction(
                accountGroupId,
                automationActionService,
                currentUserId,
                'Company',
                'COMPANY.LIST_OBJECTS_TABLE.ACTIONS.RUN_BULK_ACTION',
              ),
            );
          }
        }
        return actions;
      },
    ),
  );
}

export function buildCompanyMultiRowTableActions(
  accountGroupService: AccountGroupService,
  featureFlagService: FeatureFlagService,
  translationSvc: TranslateService,
  listActionsService: ListActionsService,
  accessService: CRMAccessService,
  automationActionService: AutomationActionsService,
  currentUserId$: Observable<string>,
): Observable<MultiRowAction[]> {
  return accountGroupService.currentAccountGroup$.pipe(
    switchMap((currentAccountGroup) => {
      return combineLatest([
        accountGroupService.currentAccountGroupId$,
        featureFlagService.checkFeatureFlagsMulti(currentAccountGroup.partnerId, '', [
          DynamicListsFeature,
          EphemeralAutomationsFeature,
        ]),
        accessService.canRunManualAutomations$,
        currentUserId$,
      ]);
    }),
    map(([accountGroupId, featureFlags, canRunAutomations, currentUserId]) => {
      const canAddToList = featureFlags[DynamicListsFeature];
      const canRunEphemeralAutomations = featureFlags[EphemeralAutomationsFeature];
      const actions: MultiRowAction[] = [];
      if (canAddToList) {
        actions.push(
          AddToListMultiRowAction(
            accountGroupId,
            listActionsService,
            'Company',
            translationSvc.instant('CRM.ADD_TO_STATIC_LIST'),
          ),
        );
      }
      if (canRunAutomations) {
        actions.push(
          AutomationMultiRowAction(
            accountGroupId,
            automationActionService,
            'Company',
            'COMPANY.LIST_OBJECTS_TABLE.ACTIONS.START_AUTOMATION',
          ),
        );
        if (canRunEphemeralAutomations) {
          actions.push(
            EphemeralAutomationMultiRowAction(
              accountGroupId,
              automationActionService,
              currentUserId,
              'Company',
              'COMPANY.LIST_OBJECTS_TABLE.ACTIONS.RUN_BULK_ACTION',
            ),
          );
        }
      }
      return actions;
    }),
  );
}

export function buildCompanySelectAllTableActions(
  accountGroupService: AccountGroupService,
  featureFlagService: FeatureFlagService,
  translationSvc: TranslateService,
  listActionsService: ListActionsService,
  bulkDeleteService: BulkDeleteService,
  exportActionsService: ExportActionsService,
  accessService: CRMAccessService,
  automationActionService: AutomationActionsService,
  currentUserId$: Observable<string>,
): Observable<MultiRowAction[]> {
  return accountGroupService.currentAccountGroup$.pipe(
    switchMap((currentAccountGroup) => {
      return combineLatest([
        accountGroupService.currentAccountGroupId$,
        featureFlagService.checkFeatureFlagsMulti(currentAccountGroup.partnerId, '', [
          DynamicListsFeature,
          EphemeralAutomationsFeature,
        ]),
        accessService.canRunManualAutomations$,
        currentUserId$,
      ]);
    }),
    map(([accountGroupId, featureFlags, canRunAutomations, currentUserId]) => {
      const canAddToList = featureFlags[DynamicListsFeature];
      const canRunEphemeralAutomations = featureFlags[EphemeralAutomationsFeature];
      const actions: MultiRowAction[] = [];
      if (canAddToList) {
        actions.push(
          AddToListSelectAllAction(
            accountGroupId,
            listActionsService,
            'Company',
            translationSvc.instant('CRM.ADD_TO_STATIC_LIST'),
          ),
        );
      }
      actions.push(BulkDeleteAll(accountGroupId, bulkDeleteService, 'Company', 'LIST_OBJECTS_TABLE.BULK_DELETE.LABEL'));
      actions.push(
        ExportSelectAllRowAction(accountGroupId, exportActionsService, 'Company', 'ACTIONS.CRM.EXPORT.LABEL'),
      );
      if (canRunAutomations) {
        actions.push(
          AutomationSelectAllRowAction(
            accountGroupId,
            automationActionService,
            'Company',
            'COMPANY.LIST_OBJECTS_TABLE.ACTIONS.START_AUTOMATION',
          ),
        );
        if (canRunEphemeralAutomations) {
          actions.push(
            EphemeralAutomationSelectAllRowAction(
              accountGroupId,
              automationActionService,
              currentUserId,
              'Company',
              'COMPANY.LIST_OBJECTS_TABLE.ACTIONS.RUN_BULK_ACTION',
            ),
          );
        }
      }
      return actions;
    }),
  );
}
